import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PathLine, { pathColor } from "./PathLine";
import Sphere from "./Sphere";
import { isSelected as matches, pathTouches, isEndpointOf } from "./lib/selection";
import { labelObstacles } from "./lib/overlays";
import { groupColour } from "./lib/palette";
import OverlayLayer, { SequenceLayer, SequenceLabels } from "./OverlayLayer";
import { withVeilHeadroom } from "./lib/veilGeometry";
import LabelLayer from "./Labels";

// One SVG, one viewBox, and the viewBox comes from the payload — the Gra tree
// may not fit the same box. The browser scales the whole coordinate system, so
// there is no maths tying element size to container size.
// Without paths the pillars have to carry the structure, so they are banded.
// This is the one place the two modes should look genuinely different, and that
// is a feature: it signals two systems rather than one dataset with a skin on.
function pillarBands(sephiroth, radius) {
  const byPillar = new Map();
  sephiroth.forEach((s) => {
    const group = byPillar.get(s.pillar) || [];
    group.push(s);
    byPillar.set(s.pillar, group);
  });

  return [...byPillar.entries()].map(([pillar, nodes]) => {
    const ys = nodes.map((n) => n.y);
    const x = nodes[0].x;
    return {
      pillar,
      x: x - radius - 4,
      y: Math.min(...ys) - radius - 4,
      width: (radius + 4) * 2,
      height: Math.max(...ys) - Math.min(...ys) + (radius + 4) * 2,
      centreX: x
    };
  });
}

export default function TreeDiagram({
  sephiroth,
  paths,
  viewBox,
  scale,
  selected,
  onSelect,
  showLabels,
  showPathLabels,
  shapeOverlays = [],
  sequenceOverlay = null,
  steps = [],
  step = 1,
  sephirothBySlug,
  pathsBySlug,
  sequenceState = null,
  sphereInscription = "number"
}) {
  // Ten spheres plus twenty-two paths would be thirty-two tab stops. One
  // roving tabIndex instead: the diagram is a single stop and arrows move
  // within it.
  const order = useMemo(
    () => [
      ...sephiroth.map((s) => ({ type: "sephirah", slug: s.slug })),
      ...paths.map((p) => ({ type: "path", slug: p.slug }))
    ],
    [sephiroth, paths]
  );

  const pathless = paths.length === 0;

  const selectedPath = useMemo(
    () =>
      selected && selected.type === "path"
        ? paths.find((p) => p.slug === selected.slug) || null
        : null,
    [selected, paths]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const nodeRefs = useRef(new Map());
  const keyOf = (node) => `${node.type}:${node.slug}`;

  // A selection made anywhere else — a table row, a deep link — moves the
  // roving focus with it, so the two never disagree.
  useEffect(() => {
    if (!selected) return;
    const index = order.findIndex(
      (n) => n.type === selected.type && n.slug === selected.slug
    );
    if (index >= 0) setActiveIndex(index);
  }, [selected, order]);

  const moveTo = useCallback(
    (index) => {
      const wrapped = (index + order.length) % order.length;
      const node = order[wrapped];
      setActiveIndex(wrapped);
      const element = nodeRefs.current.get(keyOf(node));
      if (element) element.focus();
      onSelect(node);
    },
    [order, onSelect]
  );

  const handleKeyDown = useCallback(
    (event, index, node) => {
      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          onSelect(node);
          break;
        // Spheres first in descent order, then the paths by number.
        case "ArrowDown":
        case "ArrowRight":
          event.preventDefault();
          moveTo(index + 1);
          break;
        case "ArrowUp":
        case "ArrowLeft":
          event.preventDefault();
          moveTo(index - 1);
          break;
        case "Home":
          event.preventDefault();
          moveTo(0);
          break;
        case "End":
          event.preventDefault();
          moveTo(order.length - 1);
          break;
        default:
          break;
      }
    },
    [moveTo, onSelect, order.length]
  );

  const registerRef = (node) => (element) => {
    if (element) nodeRefs.current.set(keyOf(node), element);
    else nodeRefs.current.delete(keyOf(node));
  };

  const markerGroups = useMemo(
    () =>
      shapeOverlays
        .filter((o) => o.channel === "marker")
        .flatMap((o) =>
          o.groups
            .filter((g) => g.markerY !== null && g.markerY !== undefined)
            .map((g) => ({ group: g, colour: groupColour(g) }))
        ),
    [shapeOverlays]
  );

  // The payload's box is padded for the name labels that hang outside the
  // circles. When those labels are dropped, that padding is 40% of the width on
  // a phone, all of it empty — so the window tightens onto the circles.
  // PRESENTATION ONLY: no sphere coordinate is touched.
  //
  // The negative veils then push the top edge back out, because they are drawn
  // above Kether and the pinned box has no room for them. Both adjustments move
  // the WINDOW; neither moves anything in it.
  const box = useMemo(() => {
    const groups = markerGroups.map(({ group }) => group);
    if (showLabels) return withVeilHeadroom(viewBox, groups);
    const r = viewBox.radius;
    const xs = sephiroth.map((s) => s.x);
    const ys = sephiroth.map((s) => s.y);
    // r + 5, not r: the invisible hit target is r + 4 and the pillar bands are
    // r + 4 wide, so a tighter margin would clip a touch target at the edges.
    const margin = r + 5;
    const left = Math.min(...xs) - margin;
    const top = Math.min(...ys) - margin;
    return withVeilHeadroom(
      {
        x: left,
        y: top,
        width: Math.max(...xs) + margin - left,
        height: Math.max(...ys) + margin - top,
        radius: r
      },
      groups
    );
  }, [viewBox, sephiroth, showLabels, markerGroups]);

  const { x, y, width, height, radius } = box;

  // Memoised: each of these is a fresh Set or Array on every render otherwise,
  // and the label layer below takes them as props.
  const dimmedPaths = useMemo(
    () =>
      new Set(
        selected && selected.type === "sephirah"
          ? paths.filter((p) => !pathTouches(p, selected)).map((p) => p.slug)
          : []
      ),
    [paths, selected]
  );
  const litPaths = useMemo(
    () => new Set(sequenceState ? sequenceState.paths : []),
    [sequenceState]
  );
  const obstacles = useMemo(
    () =>
      labelObstacles({
        paths: showPathLabels ? paths : [],
        sephiroth: showLabels ? sephiroth : [],
        markerGroups,
        radius,
        viewBox: box
      }),
    [showPathLabels, paths, showLabels, sephiroth, markerGroups, radius, box]
  );

  return (
    <svg
      viewBox={`${x} ${y} ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className="tree__svg"
      role="group"
      aria-label={
        pathless
          ? `Tree of Life. ${sephiroth.length} nodes in three pillars. The path arrangement is not included.`
          : `Tree of Life. ${sephiroth.length} sephiroth joined by ${paths.length} paths.`
      }
    >
      {/* Clicking the ground clears the selection. */}
      <rect
        className="tree__ground"
        x={x}
        y={y}
        width={width}
        height={height}
        onClick={() => onSelect(null)}
        data-testid="tree-ground"
      />

      {/* Bands, hulls and markers sit under everything the tree itself draws. */}
      <OverlayLayer
        overlays={shapeOverlays}
        sephirothBySlug={sephirothBySlug}
        viewBox={box}
        selected={selected}
        onSelectGroup={(group) => onSelect({ type: "marker", slug: group.slug })}
      />

      {pathless && (
        <g className="tree__pillars" aria-hidden="true" data-testid="pillar-bands">
          {pillarBands(sephiroth, radius).map((band) => (
            <g key={band.pillar} data-testid={`pillar-${band.pillar}`}>
              <rect
                className="pillar__band"
                x={band.x}
                y={band.y}
                width={band.width}
                height={band.height}
                rx={band.width / 2}
              />
              <line
                className="pillar__rule"
                x1={band.centreX}
                y1={band.y}
                x2={band.centreX}
                y2={band.y + band.height}
              />
            </g>
          ))}
        </g>
      )}

      {/* Under a sequence the path network recedes, so the descent reads. */}
      <g className={sequenceOverlay ? "tree__paths tree__paths--muted" : "tree__paths"}>
        {paths.map((path, i) => (
          <PathLine
            key={path.slug}
            path={path}
            scale={scale}
            isSelected={matches(selected, "path", path.slug)}
            dimmed={
              Boolean(selected) &&
              selected.type === "sephirah" &&
              !pathTouches(path, selected)
            }
            inSequence={Boolean(sequenceState && sequenceState.paths.has(path.slug))}
            isSequenceCurrent={Boolean(
              sequenceState && sequenceState.currentPathSlug === path.slug
            )}
            sequenceColour={sequenceState && sequenceState.colour}
            tabIndex={activeIndex === sephiroth.length + i ? 0 : -1}
            onSelect={() => {
              setActiveIndex(sephiroth.length + i);
              onSelect({ type: "path", slug: path.slug });
            }}
            onKeyDown={(event) =>
              handleKeyDown(event, sephiroth.length + i, {
                type: "path",
                slug: path.slug
              })
            }
            nodeRef={registerRef({ type: "path", slug: path.slug })}
          />
        ))}
      </g>

      {/* The trace runs UNDER the spheres, as the reference SVG draws it —
          over the top it covers the numerals. */}
      <SequenceLayer
        overlay={sequenceOverlay}
        steps={steps}
        step={step}
        hasPathNetwork={paths.length > 0}
        radius={radius}
        layer="under"
      />

      <g className="tree__spheres">
        {sephiroth.map((sephirah, index) => (
          <Sphere
            key={sephirah.slug}
            sephirah={sephirah}
            radius={radius}
            scale={scale}
            isSelected={matches(selected, "sephirah", sephirah.slug)}
            isEndpoint={isEndpointOf(sephirah, selected, paths)}
            inSequence={Boolean(sequenceState && sequenceState.ringed.has(sephirah.slug))}
            isSequenceCurrent={Boolean(
              sequenceState && sequenceState.currentSphereSlug === sephirah.slug
            )}
            sequenceColour={sequenceState && sequenceState.colour}
            tabIndex={activeIndex === index ? 0 : -1}
            onSelect={() => {
              setActiveIndex(index);
              onSelect({ type: "sephirah", slug: sephirah.slug });
            }}
            onKeyDown={(event) =>
              handleKeyDown(event, index, { type: "sephirah", slug: sephirah.slug })
            }
            nodeRef={registerRef({ type: "sephirah", slug: sephirah.slug })}
          />
        ))}
      </g>

      {/* The selected path, REPAINTED above the spheres.
          Paths draw before spheres because SVG has no z-index, which means the
          selection highlight on a path runs under the two circles at its ends
          and is clipped by both — exactly where the eye looks to see what is
          selected. This is the documented rule; it had never been built.
          Non-interactive: the original below still owns the hit target and the
          tab stop, so there is still ONE of each. */}
      {selectedPath && (
        <g className="tree__paths tree__paths--over" aria-hidden="true" data-testid="selected-path-over">
          <line
            className="path__line path__line--selected-over"
            x1={selectedPath.x1}
            y1={selectedPath.y1}
            x2={selectedPath.x2}
            y2={selectedPath.y2}
            stroke={pathColor(selectedPath, scale)}
            pointerEvents="none"
          />
        </g>
      )}

      {/* Only the cursor sits above the spheres — it has to read on top of
          whichever sphere the step is on. */}
      <SequenceLayer
        overlay={sequenceOverlay}
        steps={steps}
        step={step}
        hasPathNetwork={paths.length > 0}
        radius={radius}
        layer="over"
      />

      {/* Layer 6: every text element in the diagram, painted last. */}
      <LabelLayer
        sephiroth={sephiroth}
        paths={paths}
        radius={radius}
        scale={scale}
        showNames={showLabels}
        showPathLabels={showPathLabels}
        dimmedPaths={dimmedPaths}
        litPaths={litPaths}
        markerGroups={markerGroups}
        viewBox={box}
        sphereInscription={sphereInscription}
        selected={selected}
        onSelectMarker={(group) => onSelect({ type: "marker", slug: group.slug })}
      >
        {/* Sequence captions, badges and endpoint notes belong in the label
            layer like every other text element. */}
        <SequenceLabels
          overlay={sequenceOverlay}
          steps={steps}
          step={step}
          hasPathNetwork={paths.length > 0}
          showCaptions={showLabels}
          pathsBySlug={pathsBySlug}
          obstacles={obstacles}
          sephiroth={sephiroth}
          radius={radius}
          viewBox={box}
        />
      </LabelLayer>
    </svg>
  );
}
